// =============================================================================
// Verify Page - 6-digit code verification
// =============================================================================
// Phase 5: Frontend Implementation
// Features: 6 individual inputs, auto-focus, paste support, auto-submit
// v6.0: Added PageBackground for zen-like background images
// =============================================================================

import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useVerifyCode, useResendCode } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth';
import { PublicNavbar } from '@/components/PublicNavbar';
import { PageBackground } from '@/components/ui/PageBackground';
import { CodeInput, type CodeInputHandle } from '@/components/ui/CodeInput';
import { formatPhoneForDisplay } from '@/lib/auth';

export function VerifyPage() {
  const codeInputRef = useRef<CodeInputHandle>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pendingIdentifier, error: storeError, setError, reset, setReturnTo } = useAuthStore();
  const { mutate: verify, isPending } = useVerifyCode();
  const { mutate: resendCode, isPending: isResending } = useResendCode();

  // v5.3: If returnTo is in our URL query (preserved from /login), make sure
  // the store also has it. This is the fallback path — useVerifyCode prefers
  // the URL value, but we want the store consistent in case anything else
  // (e.g. signup) reads from it.
  useEffect(() => {
    const returnTo = searchParams.get('returnTo');
    if (returnTo && returnTo.startsWith('/')) {
      setReturnTo(returnTo);
    }
  }, [searchParams, setReturnTo]);

  // Redirect if no pending identifier
  useEffect(() => {
    if (!pendingIdentifier) {
      navigate('/login');
    }
  }, [pendingIdentifier, navigate]);

  const handleCodeComplete = (code: string) => {
    if (!pendingIdentifier) return;
    setError(null);
    verify({
      identifier: pendingIdentifier.value,
      identifierType: pendingIdentifier.type,
      code,
    });
  };

  const handleResend = () => {
    codeInputRef.current?.reset();
    setError(null);
    resendCode();
  };

  const handleBack = () => {
    // Preserve returnTo through the back navigation so user lands on the
    // login page with the same shared-property context they came from.
    const returnTo = searchParams.get('returnTo');
    reset();
    // reset() clears the store, so re-set returnTo so the next login attempt
    // still has it as a fallback even if URL is lost.
    if (returnTo && returnTo.startsWith('/')) {
      setReturnTo(returnTo);
    }
    navigate(returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login');
  };

  // Don't render if no pending identifier
  if (!pendingIdentifier) {
    return null;
  }

  const displayIdentifier =
    pendingIdentifier.maskedValue ||
    (pendingIdentifier.type === 'phone'
      ? formatPhoneForDisplay(pendingIdentifier.value)
      : pendingIdentifier.value);

  return (
    <PageBackground>
      <div className="min-h-screen">
        <PublicNavbar />

        <div className="flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <h2 className="mt-6 text-center text-2xl font-semibold text-gray-900">
              Verify your {pendingIdentifier.type === 'phone' ? 'phone' : 'email'}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              We sent a 6-digit code to{' '}
              <span className="font-medium text-gray-900">{displayIdentifier}</span>
            </p>
          </div>

          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 text-center mb-4">
                  Enter verification code
                </label>

                <CodeInput
                  ref={codeInputRef}
                  onComplete={handleCodeComplete}
                  isLoading={isPending}
                  error={storeError}
                  autoFocus={true}
                />
              </div>

              {isPending && (
                <div className="flex justify-center items-center py-2 text-sm text-gray-500">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Verifying...
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-col items-center gap-3">
              <button
                type="button"
                onClick={handleResend}
                disabled={isResending || isPending}
                className="text-sm bg-gradient-to-r from-[#17DDAB] to-[#009FE4] bg-clip-text text-transparent hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResending ? (
                  <span className="flex items-center gap-1">
                    <svg
                      className="animate-spin h-3 w-3"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Sending...
                  </span>
                ) : (
                  "Didn't receive a code? Resend"
                )}
              </button>

              <button
                type="button"
                onClick={handleBack}
                disabled={isPending}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                <ArrowLeft className="w-3 h-3" />
                Use a different {pendingIdentifier.type === 'phone' ? 'phone number' : 'email'}
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </PageBackground>
  );
}
